import React, { SFC, useState } from 'react';
import styled, { css } from 'styled-components';
import { body2 } from '@westwing/ui-kit/typography';
import { forDesktop, forWideScreens } from '@westwing/ui-kit/breakPoints';
import { StyledPlusIcon, StyledMinusIcon } from '../../Generic/Icons/StyledIcons';
import { CategoryData } from '../../../redux/category/types';
import WideFlexContainer from '../../Generic/Containers';
import TextWrapperPadding from '../../Generic/BoxHelpers';
import HybridLink from '../../Generic/Links/HybridLink';
import useListingPageUrlState from '../../../hooks/ListingPage/useListingPageUrlState';
import { getCategoriesUrl } from '../../../urlService';

const CategoryNavContainer = styled.li<{ isRoot: boolean; hideLastItemBorder?: boolean }>`
    color: ${({ theme }) => theme.colors.charcoal60};
    padding: 15px 0 4px;

    ${({ isRoot, theme }) =>
        isRoot &&
        css`
            border-bottom: 1px solid ${theme.colors.charcoal20};
            padding: 15px 0 13.5px;
            ${forDesktop`
                padding: 0 0 15px;
                border-bottom: none;
                > a:hover {
                    text-decoration: underline;
                }
            `}
        `}

    ${({ hideLastItemBorder, isRoot }) =>
        hideLastItemBorder &&
        isRoot &&
        css`
            &:last-child {
                border-bottom: none;
            }
        `}
`;

const StyledPlusAndMinusIconWrappers = styled.div`
    display: block;
    ${forDesktop`
        display: none;
    `}
`;

const NavChildrenContainer = styled.ul`
    padding-left: 10px;
    ${forDesktop`
        padding: 15px 0 0 20px;
        > li {
            padding: 0 0 15px;
            > a:hover {
                text-decoration: underline;
            }
        }
    `}
    ${forWideScreens`
        padding-left: 30px;
    `}

    > li:last-child {
        padding-bottom: 0;
    }
`;

const MenuItem = styled(WideFlexContainer)`
    ${forDesktop`
        &:hover {
            text-decoration: underline;
        }
    `}
`;

const CategoryNameWrapper = styled.span<{ selected: boolean }>`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal100};
    ${({ selected }) =>
        selected ? forDesktop` font-family: ${({ theme }) => theme.fontBrandonMedium}; font-weight: 500; ` : ''}
`;

const convertUrl = (url: string) => {
    if (url.startsWith('http')) {
        return url;
    }

    return getCategoriesUrl(url);
};

interface Props {
    category: CategoryData;
    isRoot?: boolean;
    isDesktopView?: boolean;
    hideLastItemBorder?: boolean;
    dismissNavigationFlyOut: Function;
    t: Function;
}

export const CategoryNavItem: SFC<Props> = ({
    category,
    isRoot = true,
    isDesktopView,
    hideLastItemBorder,
    dismissNavigationFlyOut,
    t = () => {},
}): React.ReactElement => {
    const { children = [] } = category;
    const containingAllInnerCategory = isDesktopView
        ? [...children]
        : [{ name: category.allProductsText || t('All articles'), url: category.url }, ...children];
    const [showChildren, setShowChildren] = useState(false);
    const [listingPageState] = useListingPageUrlState();

    const createInnerCategoryNavItems = (): React.ReactElement[] =>
        containingAllInnerCategory.map((cat: CategoryData) => (
            <CategoryNavItem
                key={cat.url}
                isRoot={false}
                t={t}
                isDesktopView={isDesktopView}
                hideLastItemBorder={hideLastItemBorder}
                category={cat}
                dismissNavigationFlyOut={dismissNavigationFlyOut}
            />
        ));

    const hasChildren = children.length > 0;

    const buildItemLabel = (categoryItem: CategoryData) => {
        const selected = listingPageState.listingPageKey === categoryItem.url;

        return (
            <TextWrapperPadding leftPadding={0} rightPadding={10}>
                <CategoryNameWrapper selected={selected}>{categoryItem.name}</CategoryNameWrapper>
            </TextWrapperPadding>
        );
    };
    const navItemUrl = convertUrl(category.url);

    return (
        <CategoryNavContainer isRoot={isRoot} hideLastItemBorder={hideLastItemBorder}>
            {hasChildren && (
                <>
                    <MenuItem
                        onClick={() => {
                            setShowChildren(!showChildren);
                        }}
                    >
                        {isDesktopView ? (
                            <HybridLink to={navItemUrl}>{buildItemLabel(category)}</HybridLink>
                        ) : (
                            <>{buildItemLabel(category)}</>
                        )}
                        {hasChildren && (
                            <StyledPlusAndMinusIconWrappers>
                                {!showChildren ? <StyledPlusIcon data-testid="plus" /> : <StyledMinusIcon />}
                            </StyledPlusAndMinusIconWrappers>
                        )}
                    </MenuItem>
                    {showChildren && <NavChildrenContainer>{createInnerCategoryNavItems()}</NavChildrenContainer>}
                </>
            )}
            {!hasChildren && <HybridLink to={navItemUrl}>{buildItemLabel(category)}</HybridLink>}
        </CategoryNavContainer>
    );
};

export default CategoryNavItem;
